import './App.css';
import { useEffect, useState } from 'react';
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { allPages, pageGroups, pagesByPath, profileTargets, SITE } from './siteData.mjs';
import { addDigitsInNumber, calculateZodiac, convertLetterToNumberChaldean } from './utilities/functions';

const analyticsId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;

const topNav = [
  { path: '/zodiac', label: 'Zodiac' },
  { path: '/tarot', label: 'Tarot' },
  { path: '/numerology', label: 'Numerology' },
  { path: '/chinese_zodiac', label: 'Chinese Zodiac' },
  { path: '/blog', label: 'Blog' },
];

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const years = Array.from({ length: 100 }, (_, index) => String(2026 - index));
const days = Array.from({ length: 31 }, (_, index) => String(index + 1));

function App() {
  return (
    <BrowserRouter>
      <SiteShell />
    </BrowserRouter>
  );
}

function SiteShell() {
  const [consent, setConsent] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage.getItem('cosmic-secrets-consent');
  });

  return (
    <>
      <ScrollToTop />
      <Analytics consent={consent} />
      <CookieBanner consent={consent} setConsent={setConsent} />
      <Routes>
        {allPages.map((page) => (
          <Route key={page.path} path={page.path} element={<PageRenderer page={page} />} />
        ))}
        <Route path="/chaldean_number/:number" element={<LegacyChaldeanRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function Analytics({ consent }) {
  const location = useLocation();

  useEffect(() => {
    if (!analyticsId) {
      return;
    }

    const disabledKey = `ga-disable-${analyticsId}`;
    window[disabledKey] = consent !== 'accepted';

    if (consent === 'accepted') {
      ReactGA.initialize(analyticsId);
    }
  }, [consent]);

  useEffect(() => {
    if (!analyticsId || consent !== 'accepted') {
      return;
    }

    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
      title: document.title,
    });
  }, [consent, location.pathname]);

  return null;
}

function CookieBanner({ consent, setConsent }) {
  useEffect(() => {
    if (!analyticsId) {
      return;
    }

    const disabledKey = `ga-disable-${analyticsId}`;
    window[disabledKey] = consent !== 'accepted';
  }, [consent]);

  if (consent) {
    return null;
  }

  function choose(value) {
    window.localStorage.setItem('cosmic-secrets-consent', value);
    setConsent(value);
  }

  return (
    <aside className="cookie-banner" aria-label="Cookie consent">
      <div className="cookie-banner__inner">
        <p className="cookie-banner__title">Use optional analytics cookies?</p>
        <p className="cookie-banner__copy">
          You can read every guide without optional tracking. Analytics only helps us see which pages readers return to most.
        </p>
        <div className="cookie-banner__actions">
          <button className="button button--primary" type="button" onClick={() => choose('accepted')}>
            Accept analytics
          </button>
          <button className="button button--ghost" type="button" onClick={() => choose('declined')}>
            Decline
          </button>
        </div>
      </div>
    </aside>
  );
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function PageRenderer({ page }) {
  return (
    <>
      <Seo page={page} />
      <div className="site-shell">
        <SiteHeader />
        <main className="site-main">
          {page.path === '/' ? <HomePage page={page} /> : <GenericPage page={page} />}
        </main>
        <SiteFooter />
      </div>
    </>
  );
}

function SiteHeader() {
  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Link className="brand" to="/">
          <span className="brand__mark" aria-hidden="true">
            ✦
          </span>
          <span>
            <span className="brand__name">{SITE.name}</span>
            <span className="brand__tagline">{SITE.tagline}</span>
          </span>
        </Link>
        <nav className="primary-nav" aria-label="Primary">
          {topNav.map((item) => (
            <NavLink key={item.path} className={({ isActive }) => `primary-nav__link${isActive ? ' is-active' : ''}`} to={item.path}>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}

function HomePage({ page }) {
  return (
    <>
      <Hero page={page} />
      <div className="page-grid page-grid--home">
        <section className="profile-panel surface">
          <div className="section-kicker">Free profile tool</div>
          <h2>Find the guides that match your sign and numbers</h2>
          <p>
            Calculate your zodiac sign, simplified life path number, and Chaldean name number.
          </p>
          <ProfileTool />
        </section>
        <aside className="spotlight-panel surface">
          <div className="section-kicker">Where to begin</div>
          <h2>Which path will you choose?</h2>
          <ul className="feature-list">
            <li>Start with Zodiac if you want personality, compatibility, and growth patterns.</li>
            <li>Start with Tarot if you want card meanings, symbolism, and reading guidance.</li>
            <li>Start with Numerology if you want to understand Life Path numbers and number symbolism.</li>
            <li>Start with Chinese Zodiac if you want to explore the animal signs in a deeper way.</li>
          </ul>
        </aside>
      </div>
      {page.featured?.length ? <CardSection title="Start with a core topic" cards={page.featured} /> : null}
      <SectionStack sections={page.sections} />
    </>
  );
}

function GenericPage({ page }) {
  return (
    <article className="content-page">
      <Hero page={page} />
      <div className="page-grid">
        <div className="page-column">
          {page.intro ? <LeadBlock intro={page.intro} /> : null}
          {page.kind === 'collection' && page.cards?.length ? <CardSection title="Explore this topic" cards={page.cards} /> : null}
          <SectionStack sections={page.sections} />
        </div>
        <aside className="page-sidebar">
          {page.facts?.length ? <FactCard facts={page.facts} /> : null}
          <GuideCard page={page} />
          {page.related?.length ? <RelatedCard paths={page.related} /> : null}
        </aside>
      </div>
    </article>
  );
}

function Hero({ page }) {
  const hasMedia = Boolean(page.image);
  return (
    <section className="hero surface">
      <Breadcrumbs items={page.breadcrumbs} />
      <div className={`hero__inner${hasMedia ? ' hero__inner--with-media' : ''}`}>
        <div className="hero__content">
          {page.eyebrow ? <p className="section-kicker">{page.eyebrow}</p> : null}
          <h1>{page.title}</h1>
          {page.heroSummary ? <p className="hero__summary">{page.heroSummary}</p> : null}
          <div className="hero__meta">
            <span>Updated {formatDate(page.updated)}</span>
            <span>{page.readingTime} min read</span>
          </div>
        </div>
        {hasMedia ? (
          <figure className="hero__media">
            <img src={page.image} alt={page.imageAlt || page.title} />
          </figure>
        ) : null}
      </div>
    </section>
  );
}

function LeadBlock({ intro }) {
  return (
    <section className="surface section-block">
      <p className="lead-text">{intro}</p>
    </section>
  );
}

function Breadcrumbs({ items }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, index) => (
        <span key={item.path}>
          {index > 0 ? <span className="breadcrumbs__divider">/</span> : null}
          {index === items.length - 1 ? (
            <span className="breadcrumbs__current">{item.name}</span>
          ) : (
            <Link to={item.path}>{item.name}</Link>
          )}
        </span>
      ))}
    </nav>
  );
}

function CardSection({ title, cards }) {
  return (
    <section className="surface section-block">
      <h2>{title}</h2>
      <div className="card-grid">
        {cards.map((card) => (
          <Link key={`${card.path}-${card.title}`} className="topic-card" to={card.path}>
            {card.image ? (
              <span className="topic-card__image-wrap">
                <img className="topic-card__image" src={card.image} alt={card.imageAlt || card.title} />
              </span>
            ) : null}
            <span className="topic-card__icon" aria-hidden="true">
              {card.icon || '✦'}
            </span>
            <span className="topic-card__title">{card.title}</span>
            <span className="topic-card__summary">{card.summary}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function sectionId(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function SectionStack({ sections }) {
  if (!sections?.length) {
    return null;
  }

  return (
    <div className="section-stack">
      {sections.map((section) => (
        <section key={section.title} id={sectionId(section.title)} className="surface section-block">
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </section>
      ))}
    </div>
  );
}

function FactCard({ facts }) {
  return (
    <section className="surface side-card">
      <h2>Quick facts</h2>
      <dl className="fact-list">
        {facts.map(([label, value]) => (
          <div key={label} className="fact-list__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function GuideCard({ page }) {
  return (
    <section className="surface side-card">
      <h2>In this guide</h2>
      <p>{page.description}</p>
      {page.sections?.length ? (
        <div className="guide-links">
          {page.sections.map((section) => (
            <a key={section.title} className="guide-links__link" href={`#${sectionId(section.title)}`}>
              {section.title}
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function RelatedCard({ paths }) {
  return (
    <section className="surface side-card">
      <h2>Keep exploring</h2>
      <div className="related-list">
        {paths
          .filter(Boolean)
          .map((path) => pagesByPath[path])
          .filter(Boolean)
          .map((page) => (
            <Link key={page.path} className="related-list__link" to={page.path}>
              {page.title}
            </Link>
          ))}
      </div>
    </section>
  );
}

function ProfileTool() {
  const [form, setForm] = useState({
    name: '',
    month: '',
    day: '',
    year: '',
  });
  const [result, setResult] = useState(null);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (!form.name || !form.month || !form.day || !form.year) {
      return;
    }

    const zodiac = calculateZodiac(form.month, Number(form.day));
    const lifePath = reduceToSingleDigit(Number(`${monthNames.indexOf(form.month) + 1}${form.day}${form.year}`));
    const chaldean = reduceToSingleDigit(
      form.name
        .split('')
        .map((character) => convertLetterToNumberChaldean(character))
        .filter((value) => typeof value === 'number')
        .reduce((total, value) => total + value, 0),
    );

    setResult({
      zodiac,
      lifePath,
      chaldean,
    });
  }

  return (
    <>
      <form className="profile-form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Full name</span>
          <input type="text" value={form.name} onChange={(event) => updateField('name', event.target.value)} placeholder="Your full name" />
        </label>
        <div className="profile-form__row">
          <label className="field">
            <span>Birth month</span>
            <select value={form.month} onChange={(event) => updateField('month', event.target.value)}>
              <option value="">Select month</option>
              {monthNames.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Day</span>
            <select value={form.day} onChange={(event) => updateField('day', event.target.value)}>
              <option value="">Day</option>
              {days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Year</span>
            <select value={form.year} onChange={(event) => updateField('year', event.target.value)}>
              <option value="">Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button className="button button--primary" type="submit">
          Generate profile
        </button>
      </form>
      {result ? <ProfileResults result={result} /> : null}
    </>
  );
}

function ProfileResults({ result }) {
  const cards = [
    {
      path: profileTargets.zodiac[result.zodiac],
      title: result.zodiac,
      summary: 'Your zodiac sign guide with personality, work, and relationship patterns.',
      icon: pageGroups.zodiacSigns.find((sign) => sign.name === result.zodiac)?.icon || '✦',
    },
    {
      path: profileTargets.lifePath[String(result.lifePath)],
      title: `Life Path ${result.lifePath}`,
      summary: 'A practical numerology read on the developmental lesson of your birthday.',
      icon: `${result.lifePath}`,
    },
    {
      path: profileTargets.chaldean[String(result.chaldean)],
      title: `Chaldean ${result.chaldean}`,
      summary: 'A symbolic reading of the Chaldean number built from your name.',
      icon: `${result.chaldean}`,
    },
  ];

  return (
    <div className="profile-results">
      <div className="section-kicker">Your matches</div>
      <div className="card-grid">
        {cards.map((card) => (
          <Link key={card.path} className="topic-card" to={card.path}>
            <span className="topic-card__icon" aria-hidden="true">
              {card.icon}
            </span>
            <span className="topic-card__title">{card.title}</span>
            <span className="topic-card__summary">{card.summary}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function LegacyChaldeanRoute() {
  const params = useParams();
  const number = params.number;
  const page = pagesByPath[`/chaldean_number_${number}`];

  if (!page) {
    return <NotFoundPage />;
  }

  return <PageRenderer page={page} />;
}

function NotFoundPage() {
  const page = {
    title: '404 page not found',
    description: 'The page you requested could not be found. Start again from one of the core guide hubs.',
    updated: SITE.updated,
    readingTime: 1,
    noindex: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: '404', path: '/404' },
    ],
    sections: [
      {
        title: 'Try one of the main guide hubs',
        body: ['The requested page may have moved during the site rebuild. The strongest paths back into the content are Zodiac, Tarot, Numerology, and Chinese Zodiac.'],
      },
    ],
    related: ['/zodiac', '/tarot', '/numerology', '/chinese_zodiac'],
  };

  return (
    <>
      <Seo page={page} />
      <div className="site-shell">
        <SiteHeader />
        <main className="site-main">
          <article className="content-page">
            <Hero page={page} />
            <div className="page-grid">
              <div className="page-column">
                <SectionStack sections={page.sections} />
              </div>
              <aside className="page-sidebar">
                <RelatedCard paths={page.related} />
              </aside>
            </div>
          </article>
        </main>
        <SiteFooter />
      </div>
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div>
          <p className="site-footer__heading">Core guides</p>
          <div className="site-footer__links">
            <Link to="/zodiac">Zodiac</Link>
            <Link to="/tarot">Tarot</Link>
            <Link to="/numerology">Numerology</Link>
            <Link to="/chinese_zodiac">Chinese Zodiac</Link>
          </div>
        </div>
        <div>
          <p className="site-footer__heading">Editorial</p>
          <div className="site-footer__links">
            <Link to="/about">About</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy_policy">Privacy</Link>
          </div>
        </div>
        <div className="site-footer__note">
          <p className="site-footer__heading">{SITE.name}</p>
          <p>Long-form guides on tarot, zodiac, numerology, and the Chinese zodiac.</p>
          <p className="site-footer__copyright">© 2026 {SITE.domain}</p>
        </div>
      </div>
    </footer>
  );
}

function Seo({ page }) {
  const location = useLocation();

  useEffect(() => {
    const canonicalUrl = page.canonicalUrl || `${SITE.url}${location.pathname}`;
    const pageTitle = page.seoTitle || page.title;
    const structuredData = buildStructuredData(page);
    const metaMap = {
      description: page.description,
      'theme-color': SITE.themeColor,
      robots: page.noindex ? 'noindex,follow' : 'index,follow',
      'og:title': pageTitle,
      'og:description': page.description,
      'og:type': page.kind === 'article' ? 'article' : 'website',
      'og:url': canonicalUrl,
      'og:image': page.image || SITE.defaultImage,
      'twitter:card': 'summary_large_image',
      'twitter:title': pageTitle,
      'twitter:description': page.description,
      'twitter:image': page.image || SITE.defaultImage,
    };

    document.title = pageTitle;
    document.documentElement.lang = 'en';

    Object.entries(metaMap).forEach(([name, content]) => {
      setMetaTag(name, content);
    });

    setLinkTag('canonical', canonicalUrl);

    document.querySelectorAll('script[data-cosmic-schema="true"]').forEach((node) => node.remove());

    structuredData.forEach((entry) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.cosmicSchema = 'true';
      script.text = JSON.stringify(entry);
      document.head.appendChild(script);
    });
  }, [location.pathname, page]);

  return null;
}

function setMetaTag(name, content) {
  const selector = name.startsWith('og:') ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    if (name.startsWith('og:')) {
      tag.setAttribute('property', name);
    } else {
      tag.setAttribute('name', name);
    }
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setLinkTag(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function buildStructuredData(page) {
  const websiteId = `${SITE.url}#website`;
  const organizationId = `${SITE.url}#organization`;
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: page.breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE.url}${item.path}`,
    })),
  };
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,
    name: SITE.publisher,
    url: SITE.url,
    logo: {
      '@type': 'ImageObject',
      url: SITE.defaultImage,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'editorial inquiries',
      email: SITE.email,
      availableLanguage: 'en',
    },
  };

  if (page.path === '/') {
    return [
      organization,
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': websiteId,
        url: SITE.url,
        name: SITE.name,
        description: SITE.description,
        publisher: { '@id': organizationId },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: page.title,
        url: page.canonicalUrl,
        description: page.description,
        about: ['Tarot', 'Astrology', 'Numerology', 'Chinese zodiac'],
        isPartOf: { '@id': websiteId },
        breadcrumb: {
          '@id': `${page.canonicalUrl}#breadcrumb`,
        },
      },
      {
        ...breadcrumb,
        '@id': `${page.canonicalUrl}#breadcrumb`,
      },
    ];
  }

  if (page.path === '/faq') {
    return [
      organization,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        name: page.title,
        url: page.canonicalUrl,
        description: page.description,
        mainEntity: page.sections.map((section) => ({
          '@type': 'Question',
          name: section.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: section.body.join(' '),
          },
        })),
      },
      breadcrumb,
    ];
  }

  if (page.path === '/contact') {
    return [
      organization,
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: page.title,
        url: page.canonicalUrl,
        description: page.description,
      },
      breadcrumb,
    ];
  }

  if (page.path === '/about') {
    return [
      organization,
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: page.title,
        url: page.canonicalUrl,
        description: page.description,
        about: SITE.name,
      },
      breadcrumb,
    ];
  }

  if (page.kind === 'collection') {
    return [
      organization,
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: page.title,
        url: page.canonicalUrl,
        description: page.description,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: (page.cards || []).map((card, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${SITE.url}${card.path}`,
            name: card.title,
          })),
        },
      },
      breadcrumb,
    ];
  }

  return [
    organization,
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.title,
      description: page.description,
      image: page.image || SITE.defaultImage,
      mainEntityOfPage: page.canonicalUrl,
      url: page.canonicalUrl,
      datePublished: SITE.updated,
      dateModified: page.updated,
      author: {
        '@type': 'Organization',
        name: SITE.publisher,
      },
      publisher: {
        '@type': 'Organization',
        name: SITE.publisher,
        logo: {
          '@type': 'ImageObject',
          url: SITE.defaultImage,
        },
      },
      articleSection: page.eyebrow || 'Guide',
      isAccessibleForFree: true,
    },
    breadcrumb,
  ];
}

function reduceToSingleDigit(number) {
  let value = Math.abs(Number(number)) || 0;
  while (value >= 10) {
    value = addDigitsInNumber(value);
  }
  return value;
}

function formatDate(value) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default App;
